import { motion, useReducedMotion } from "motion/react";
import { principles } from "../../content";

/* Indent cycle drifts the rows across the page like a plotted course. */
const indentClasses = ["md:pl-0", "md:pl-24", "md:pl-48"] as const;

/* Dashed course segments joining one row's ghost number to the next.
 * Row indents step by 6rem (96px) between consecutive rows, except gap 2,
 * which doubles back from pl-48 to pl-0; its svg shifts left by 12rem so
 * the return leg stays inside the viewBox. Coordinates assume the number
 * glyphs sit roughly 64px into each row, close enough for a decorative line.
 */
const connectors = [
  { d: "M64 6C64 30 160 26 160 50", className: "" },
  { d: "M64 6C64 30 160 26 160 50", className: "" },
  { d: "M256 6C256 30 64 26 64 50", className: "md:-ml-48" },
  { d: "M64 6C64 30 160 26 160 50", className: "" },
] as const;

/** Five principles set as a staggered ledger. No heading; the rows speak. */
export function Philosophy() {
  const reduce = useReducedMotion();
  const reveal = (i: number) => ({
    initial: reduce ? false : { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.65, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] as const },
  });

  return (
    <section className="bg-paper py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <ol className="space-y-12 md:space-y-3">
          {principles.map((principle, i) => (
            <motion.li
              key={principle.title}
              {...reveal(i)}
              className={indentClasses[i % indentClasses.length]}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-baseline md:gap-10">
                {/* Ghost number: solid line-colour text by default, so browsers
                    without -webkit-text-stroke still show a legible numeral;
                    supporting browsers hollow it out into an outline. */}
                <span className="display-type text-[clamp(3rem,8vw,7rem)] text-line select-none [-webkit-text-stroke:2px_var(--color-line)] supports-[-webkit-text-stroke]:text-transparent">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <p className="display-type text-[clamp(1.4rem,2.6vw,2.2rem)] text-ink">
                    {principle.title}
                  </p>
                  <p className="mt-2 max-w-[44ch] text-ink-soft">{principle.line}</p>
                </div>
              </div>

              {i < principles.length - 1 && (
                <svg
                  viewBox="0 0 320 56"
                  aria-hidden
                  focusable="false"
                  className={`mt-5 hidden h-14 w-80 md:block ${connectors[i].className}`}
                >
                  <path
                    d={connectors[i].d}
                    fill="none"
                    className="stroke-line"
                    strokeWidth="1.5"
                    strokeDasharray="7 9"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
